// OpenPGP is the one family here that carries its own session key, its own algorithm identifiers and its own armour,
// so nothing on this page picks a key length or a nonce for it. The library is imported dynamically for the reason
// /keygen imports it that way: it is the largest thing either page can reach and most visits never reach it.
export interface PgpSettings {
  // A message is encrypted to a public key or to a password, and read back with a private key or the same password.
  recipient: "key" | "password";
  publicKey: string;
  privateKey: string;
  passphrase: string;
  password: string;
}

export async function pgpEncrypt(message: Message, settings: PgpSettings): Promise<string | Uint8Array> {
  const openpgp = await import("openpgp");
  const encryptionKeys = settings.recipient === "key" ? await readPublicKeys(openpgp, settings.publicKey) : undefined;
  const passwords = settings.recipient === "password" ? [settings.password] : undefined;
  const options = { message: await createMessage(openpgp, message), encryptionKeys, passwords };
  // Armour for something being pasted somewhere, and the packets themselves for something being saved as a file. The
  // two are spelled out rather than picked into one call, `format` being what decides which overload this is.
  return message.kind === "text"
    ? openpgp.encrypt({ ...options, format: "armored" })
    : openpgp.encrypt({ ...options, format: "binary" });
}

export async function pgpDecrypt(payload: string | Uint8Array, settings: PgpSettings): Promise<Uint8Array> {
  const openpgp = await import("openpgp");
  const message = typeof payload === "string"
    ? await openpgp.readMessage({ armoredMessage: payload })
    : await openpgp.readMessage({ binaryMessage: payload });
  const { data } = await openpgp.decrypt({
    message,
    decryptionKeys: settings.recipient === "key" ? await readPrivateKeys(openpgp, settings) : undefined,
    passwords: settings.recipient === "password" ? [settings.password] : undefined,
    format: "binary",
  });
  return data;
}

export type Message = { kind: "text"; text: string } | { kind: "binary"; bytes: Uint8Array; filename: string };

type OpenPgp = typeof import("openpgp");

function createMessage(openpgp: OpenPgp, message: Message) {
  return message.kind === "text"
    ? openpgp.createMessage({ text: message.text })
    // The name rides inside the encrypted literal packet, so whoever decrypts it is told what the file was called.
    : openpgp.createMessage({ binary: message.bytes, filename: message.filename });
}

// A block can hold several keys, and a message can be encrypted to every one of them.
async function readPublicKeys(openpgp: OpenPgp, armored: string) {
  const keys = await openpgp.readKeys({ armoredKeys: armored });
  if (keys.length === 0) throw new Error("That block holds no public key");
  return keys;
}

async function readPrivateKeys(openpgp: OpenPgp, settings: PgpSettings) {
  const keys = await openpgp.readPrivateKeys({ armoredKeys: settings.privateKey });
  if (keys.length === 0) throw new Error("That block holds no private key");
  // A key that is already decrypted takes no passphrase, and one that is not cannot be used without it.
  return Promise.all(
    keys.map((privateKey) =>
      privateKey.isDecrypted() ? privateKey : openpgp.decryptKey({ privateKey, passphrase: settings.passphrase })
    ),
  );
}
